/**
 * PLP Payments Bars — native Shop Pay installments + Extended Warranty bars.
 * Used by both the grid and list product cards (window.PlpProductCardGrid / List).
 *
 * Installments: keeps the Figma copy ("Installments with" + logo + Learn more).
 * Shop Pay SVG and the native modal come from a hidden shopify-payment-terms widget.
 *
 * Visibility is driven by window.algoliaConfig.showBuyLater / showWarranty (both
 * mirror Theme settings → Warranty & Financing → Show Warranty & Financing).
 *
 * IMPORTANT: this component is built with preact.h() directly, NOT htm `html``.
 * It used to use the shared window.preact.html (htm) instance, and that poisoned
 * htm's template cache so the header autocomplete popup (header-search-bar.js,
 * also on htm) rendered this "Extended Warranty" markup in place of its
 * Pages/Articles sections. Building with h() keeps this component out of htm's
 * cache entirely, so it can never corrupt the search popup.
 */
(function () {
  var preact = window.preact || {};
  var h = preact.h;
  var useEffect = preact.useEffect;
  var useRef = preact.useRef;
  var useState = preact.useState;
  if (!h || !useEffect) return;

  function WarrantyIcon() {
    return h(
      'svg',
      {
        class: 'plp-payments__shield',
        width: '13',
        height: '13',
        viewBox: '0 0 20 20',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
      },
      h('path', {
        d: 'M10 1.25 3.125 4.0625v4.0625c0 4.2375 2.9325 8.2 6.875 9.1875 3.9425-0.9875 6.875-4.95 6.875-9.1875V4.0625L10 1.25Z',
        stroke: '#709525',
        'stroke-width': '1.4',
        'stroke-linejoin': 'round',
      }),
      h('path', {
        d: 'm6.875 9.6875 2.0625 2.0625L13.4375 7.25',
        stroke: '#709525',
        'stroke-width': '1.4',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      })
    );
  }

  // Inline slot is shown from 1101px up (regime A ≥1361 + regime B 1101-1360);
  // the foot slot takes over at ≤1100 (the filters sidebar collapses at ≤990,
  // so 991-1100 is too narrow for inline bars). Keep in sync with plp-payments.css.
  var LIST_DESKTOP_MQ = '(min-width: 1101px)';

  function useListDesktopInline() {
    var _s = useState(function () {
      return (
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia(LIST_DESKTOP_MQ).matches
      );
    });
    var desktopInline = _s[0];
    var setDesktopInline = _s[1];

    useEffect(function () {
      if (!window.matchMedia) return;
      var mq = window.matchMedia(LIST_DESKTOP_MQ);
      function onChange() {
        setDesktopInline(mq.matches);
      }
      onChange();
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else mq.addListener(onChange);
      return function () {
        if (mq.removeEventListener) mq.removeEventListener('change', onChange);
        else mq.removeListener(onChange);
      };
    }, []);

    return desktopInline;
  }

  function shouldMountInstallments(listSlot, listDesktopInline) {
    if (!listSlot) return true;
    if (listSlot === 'inline') return listDesktopInline;
    if (listSlot === 'bottom') return !listDesktopInline;
    return true;
  }

  function PlpInstallmentsBar(props) {
    var handle = props.handle;
    var variantId = props.variantId;
    var hostRef = useRef(null);
    var logoRef = useRef(null);
    var _hidden = useState(false);
    var hidden = _hidden[0];
    var setHidden = _hidden[1];

    function openNativeModal(e) {
      e.preventDefault();
      var host = hostRef.current;
      if (!host) return;
      var cta = host._plpNativeCta;
      if (!cta) {
        var terms = host.querySelector('shopify-payment-terms');
        cta =
          terms &&
          terms.shadowRoot &&
          terms.shadowRoot.querySelector('#shopify-installments-cta');
      }
      if (cta) cta.click();
    }

    function wireNativeWidget() {
      var host = hostRef.current;
      var logo = logoRef.current;
      if (!host || !logo) return false;

      var terms = host.querySelector('shopify-payment-terms');
      if (!terms || !terms.shadowRoot) return false;

      var svg = terms.shadowRoot.querySelector('#shopify-installments-content svg');
      if (svg) {
        logo.innerHTML = '';
        var clone = svg.cloneNode(true);
        clone.removeAttribute('width');
        clone.removeAttribute('height');
        clone.setAttribute('aria-hidden', 'true');
        clone.style.display = 'block';
        clone.style.height = '11px';
        clone.style.width = 'auto';
        clone.style.maxHeight = '11px';
        logo.appendChild(clone);
      }

      var cta = terms.shadowRoot.querySelector('#shopify-installments-cta');
      if (!cta) return false;

      host._plpNativeCta = cta;
      if (window.styleShopifyPaymentTerms) window.styleShopifyPaymentTerms();
      return true;
    }

    function tryWire(attempt) {
      if (wireNativeWidget()) return;
      if (attempt >= 20) {
        setHidden(true);
        return;
      }
      setTimeout(function () {
        tryWire(attempt + 1);
      }, 100);
    }

    useEffect(function () {
      if (!handle || !variantId || !hostRef.current) return;
      var cancelled = false;
      var load = window.loadPlpPaymentTerms;
      if (!load) return;

      load(hostRef.current, handle, variantId).then(function (ok) {
        if (cancelled) return;
        if (!ok) {
          setHidden(true);
          return;
        }
        tryWire(0);
      });

      return function () {
        cancelled = true;
      };
    }, [handle, variantId]);

    if (hidden) return null;

    return h(
      'div',
      { class: 'plp-payments__block plp-payments__affirm plp-payments__affirm--shopify' },
      h(
        'span',
        { class: 'plp-payments__affirm-msg' },
        h('strong', null, 'Installments with'),
        h('span', { class: 'plp-payments__affirm-logo plp-payments__shop-logo', ref: logoRef })
      ),
      h(
        'a',
        {
          href: '#',
          class: 'plp-payments__cta plp-payments__cta--affirm',
          onClick: openNativeModal,
          role: 'button',
        },
        'Learn more'
      ),
      h('div', {
        class: 'plp-payments__shopify-host plp-payments__shopify-host--sr',
        ref: hostRef,
        'aria-hidden': 'true',
      })
    );
  }

  function staticInstallmentsBlock() {
    return h(
      'div',
      {
        class: 'plp-payments__block plp-payments__affirm plp-payments__affirm--shopify',
        key: 'installments',
      },
      h(
        'span',
        { class: 'plp-payments__affirm-msg' },
        h('strong', null, 'Installments with'),
        h('span', { class: 'plp-payments__affirm-logo plp-payments__shop-logo' })
      ),
      h(
        'span',
        { class: 'plp-payments__cta plp-payments__cta--affirm' },
        h('span', { class: 'plp-payments__cta-label' }, 'Learn more')
      )
    );
  }

  function staticWarrantyBlock() {
    return h(
      'div',
      { class: 'plp-payments__block plp-payments__warranty', key: 'warranty' },
      h(
        'span',
        { class: 'plp-payments__warranty-info' },
        h('span', { class: 'plp-payments__warranty-icon' }, h(WarrantyIcon)),
        h('span', { class: 'plp-payments__warranty-text' }, 'Extended Warranty')
      )
    );
  }

  function buildReservedPaymentBlocks(listSlot, listDesktopInline) {
    var cfg = window.algoliaConfig || {};
    var showBuyLater = !!cfg.showBuyLater;
    var showWarranty = !!cfg.showWarranty;
    var mountInstallments = showBuyLater && shouldMountInstallments(listSlot, listDesktopInline);
    var blocks = [];

    if (mountInstallments) blocks.push(staticInstallmentsBlock());
    if (showWarranty) blocks.push(staticWarrantyBlock());

    return blocks;
  }

  function PlpPaymentsBars(props) {
    var handle = props.handle;
    var variantId = props.variantId;
    var listSlot = props.listSlot;
    var layoutOnly = !!props.layoutOnly;
    var cfg = window.algoliaConfig || {};
    var showBuyLater = !!cfg.showBuyLater;
    var showWarranty = !!cfg.showWarranty;
    var listDesktopInline = useListDesktopInline();
    var mountInstallments =
      showBuyLater && handle && variantId && shouldMountInstallments(listSlot, listDesktopInline);

    if (layoutOnly) {
      var reservedBlocks = buildReservedPaymentBlocks(listSlot, listDesktopInline);
      if (!reservedBlocks.length) return null;
      return h(
        'div',
        { class: 'plp-payments plp-payments--reserved', 'aria-hidden': 'true' },
        reservedBlocks
      );
    }

    if (!mountInstallments && !showWarranty) return null;

    var blocks = [];

    if (mountInstallments) {
      blocks.push(
        h(PlpInstallmentsBar, {
          key: 'installments',
          handle: handle,
          variantId: variantId,
        })
      );
    }

    if (showWarranty) {
      blocks.push(staticWarrantyBlock());
    }

    if (!blocks.length) return null;

    return h('div', { class: 'plp-payments' }, blocks);
  }

  window.PlpPaymentsBars = PlpPaymentsBars;
})();
